/**
 * BalanceLine — C1 (home hero, 30 back + 30 forward) and C5 (cash-flow, 30 back + 90 forward).
 *
 * One series (spec §6): the actual balance is a solid 2px line with a 10% area wash, the
 * forecast continues in the same hue dashed. Event markers sit on the line; the alert floor is a
 * thin `warn` rule with a "רף" label; "היום" is a dotted vertical. Keyboard: the plot is one
 * focusable region and ←/→ walk day by day, announcing the value through the tooltip
 * (`role="status"`), exactly like the mockup.
 *
 * `compact` renders the phone sparkline: no axes, two direct labels, no hit area — the caller
 * wraps it in a single tap target.
 */

import { useRef, useState } from 'react';
import type { BalancePoint, ForecastEvent } from '../../../api-types.ts';
import { ChartBlock, type TableTwin } from './ChartBlock.tsx';
import { ChartTip, useTooltip } from './Tooltip.tsx';
import { areaPath, clamp, linePath, niceTicks } from './chart-utils.ts';
import { useChartWidth } from './useChartWidth.ts';
import { fmtK, fmtMoney, fmtShortDate } from '../format.ts';
import { he } from '../i18n/he.ts';

export interface BalanceLineProps {
  points: BalancePoint[];
  events: ForecastEvent[];
  asOf: string;
  height?: number;
  compact?: boolean;
  /** agorot — the forecast alert floor (spec §6 C5). */
  floorAgorot?: number | null;
  /** Only these event kinds get a marker. */
  eventKinds?: ForecastEvent['kind'][];
  /** Only these event kinds get a text label (the rest live in the tooltip). */
  labelKinds?: ForecastEvent['kind'][];
  summary: string;
  table: TableTwin;
}

export function BalanceLine({
  points,
  events,
  asOf,
  height = 150,
  compact = false,
  floorAgorot = null,
  eventKinds,
  labelKinds,
  summary,
  table,
}: BalanceLineProps) {
  const [ref, width] = useChartWidth(compact ? 340 : 720);
  const tooltip = useTooltip();
  const [active, setActive] = useState<number | null>(null);
  const focusIndex = useRef<number>(-1);

  const W = Math.max(width, 200);
  const mL = compact ? 4 : 44;
  const mR = compact ? 4 : 14;
  const mT = compact ? 14 : 22;
  const mB = compact ? 4 : 22;
  const pw = W - mL - mR;
  const ph = height - mT - mB;

  if (points.length < 2) return <div ref={ref} />;

  const values = points.map((p) => p.balanceAgorot);
  const ticks = niceTicks(0, Math.max(...values) * 1.08, 5);
  const x = (i: number) => mL + (i / (points.length - 1)) * pw;
  const y = (v: number) => mT + ph - ((v - 0) / (ticks.max || 1)) * ph;

  const firstForecast = points.findIndex((p) => !p.actual);
  const todayIndex = firstForecast <= 0 ? points.length - 1 : firstForecast - 1;
  const actual = points.map((p, i) => ({ i, p })).filter((e) => e.p.actual);
  const forecast = points.map((p, i) => ({ i, p })).filter((e) => !e.p.actual || e.i === todayIndex);
  const actualCoords = actual.map((e) => [x(e.i), y(e.p.balanceAgorot)] as const);
  const forecastCoords = forecast.map((e) => [x(e.i), y(e.p.balanceAgorot)] as const);

  const eventsByDay = new Map<string, ForecastEvent[]>();
  for (const event of events) {
    if (eventKinds && !eventKinds.includes(event.kind)) continue;
    const list = eventsByDay.get(event.day) ?? [];
    list.push(event);
    eventsByDay.set(event.day, list);
  }
  const markers = points
    .map((p, i) => ({ i, p, list: eventsByDay.get(p.day) ?? [] }))
    .filter((m) => m.list.length > 0);
  const labelled = markers.filter((m) => !labelKinds || m.list.some((e) => labelKinds.includes(e.kind)));
  const labelsFit = !compact && pw / Math.max(1, labelled.length) > 95;

  const last = points[points.length - 1]!;
  const today = points[todayIndex]!;

  const showAt = (index: number) => {
    const i = clamp(index, 0, points.length - 1);
    focusIndex.current = i;
    setActive(i);
    const point = points[i]!;
    const rows = [{ label: point.actual ? 'יתרה בפועל' : 'תחזית יתרה', value: fmtMoney(point.balanceAgorot), color: 'var(--series-1)' }];
    for (const event of eventsByDay.get(point.day) ?? []) rows.push({ label: `אירוע: ${event.label}`, value: fmtMoney(event.amountAgorot, 0, true), color: '' });
    tooltip.show({ title: fmtShortDate(point.day, asOf), rows: rows.filter((r) => r.label) }, x(i) * (width / W), y(point.balanceAgorot) * (width / W));
  };

  const plot = (
    <div className="plot">
      <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height} aria-hidden={compact ? true : undefined}>
        {compact ? null : (
          <>
            <g className="grid">
              {ticks.ticks.map((t) => (
                <line key={t} x1={mL} x2={W - mR} y1={y(t)} y2={y(t)} />
              ))}
            </g>
            <g className="axis">
              {ticks.ticks.map((t) => (
                <text key={t} x={mL - 6} y={y(t) + 3.5} textAnchor="end" className="lbl2">
                  {fmtK(t)}
                </text>
              ))}
            </g>
          </>
        )}

        <path d={areaPath(actualCoords, mT + ph)} fill="var(--series-1)" opacity=".1" />
        <path d={linePath(actualCoords)} fill="none" stroke="var(--series-1)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <path d={linePath(forecastCoords)} fill="none" stroke="var(--series-1)" strokeWidth="2" className="fc" strokeLinejoin="round" strokeLinecap="round" />

        {floorAgorot && !compact ? (
          <>
            <line className="thr" x1={mL} x2={W - mR} y1={y(floorAgorot)} y2={y(floorAgorot)} />
            <text x={W - mR - 2} y={y(floorAgorot) - 4} textAnchor="end" className="thr-t">
              רף {fmtMoney(floorAgorot)}
            </text>
          </>
        ) : null}

        <line className="today" x1={x(todayIndex)} x2={x(todayIndex)} y1={mT} y2={mT + ph} />

        {compact
          ? null
          : markers.map((m) => {
              const total = m.list.reduce((sum, e) => sum + e.amountAgorot, 0);
              const cx = x(m.i);
              const cy = y(m.p.balanceAgorot);
              const label = m.list.length === 1 ? m.list[0]!.label : m.list.map((e) => e.label).join(' + ');
              const ly = total > 0 ? cy - 22 : cy + 16;
              const anchor = cx < mL + 30 ? 'start' : cx > W - mR - 30 ? 'end' : 'middle';
              return (
                <g key={m.p.day}>
                  <circle className="ev" cx={cx} cy={cy} r="4" />
                  {labelsFit && labelled.includes(m) ? (
                    <>
                      <text x={cx} y={ly} textAnchor={anchor} className="ev-t">
                        {label}
                      </text>
                      <text x={cx} y={ly + 12} textAnchor={anchor} className="lbl" style={{ fontSize: '11px' }}>
                        {fmtMoney(total, 0, true)}
                      </text>
                    </>
                  ) : null}
                </g>
              );
            })}

        <circle cx={x(todayIndex)} cy={y(today.balanceAgorot)} r="4.5" fill="var(--series-1)" className="ring" />
        <circle cx={x(points.length - 1)} cy={y(last.balanceAgorot)} r="4" fill="var(--surface-1)" stroke="var(--series-1)" strokeWidth="2" />

        <text
          x={compact ? Math.min(x(todayIndex) + 6, W - 90) : x(todayIndex) + 6}
          y={compact ? y(today.balanceAgorot) - 6 : mT - 8}
          className="lbl"
          textAnchor="start"
          style={{ fontSize: '11px' }}
        >
          היום {fmtMoney(today.balanceAgorot)}
        </text>
        <text
          x={x(points.length - 1)}
          y={compact ? y(last.balanceAgorot) - 8 : mT - 8}
          className="lbl"
          textAnchor="end"
          style={{ fontSize: '11px' }}
        >
          {fmtShortDate(last.day, asOf)} {fmtMoney(last.balanceAgorot)}
        </text>

        {active != null && !compact ? (
          <g>
            <line className="xh" x1={x(active)} x2={x(active)} y1={mT} y2={mT + ph} />
            <circle cx={x(active)} cy={y(points[active]!.balanceAgorot)} r="4.5" fill="var(--series-1)" className="ring" />
          </g>
        ) : null}

        {compact ? null : (
          <rect
            className="hit"
            x={mL}
            y={mT}
            width={pw}
            height={ph}
            tabIndex={0}
            role="img"
            aria-label={summary}
            onPointerMove={(event) => {
              const rect = (event.currentTarget as SVGRectElement).ownerSVGElement!.getBoundingClientRect();
              const px = ((event.clientX - rect.left) * W) / rect.width;
              showAt(Math.round(((px - mL) / pw) * (points.length - 1)));
            }}
            onPointerLeave={() => {
              setActive(null);
              tooltip.hide();
            }}
            onFocus={() => showAt(focusIndex.current < 0 ? todayIndex : focusIndex.current)}
            onBlur={() => {
              setActive(null);
              tooltip.hide();
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                showAt(focusIndex.current + 1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                showAt(focusIndex.current - 1);
              } else if (event.key === 'Escape') {
                setActive(null);
                tooltip.hide();
              }
            }}
          />
        )}
      </svg>
    </div>
  );

  if (compact) return <div ref={ref}>{plot}</div>;

  return (
    <div ref={ref}>
      <ChartBlock
        summary={summary}
        legend={[
          { label: 'בפועל', color: 'var(--series-1)', kind: 'line' },
          { label: 'תחזית', color: 'var(--series-1)', kind: 'dash' },
        ]}
        table={table}
      >
        {plot}
        <ChartTip state={tooltip.state} width={W} />
      </ChartBlock>
    </div>
  );
}

/** The verbal summary reused by the aria-label and the mobile tap target. */
export function balanceSummary(todayAgorot: number, forecastAgorot: number, forecastDay: string, asOf: string): string {
  return `${he.home.balanceQuestion} היום ${fmtMoney(todayAgorot)}, צפי ${fmtShortDate(forecastDay, asOf)} ${fmtMoney(forecastAgorot)}.`;
}
